import { TableCell, TableHead, TableRow } from "@material-ui/core";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import { makeStyles } from "@material-ui/core/styles";
import DragHandleIcon from "@material-ui/icons/DragHandle";
import arrayMove from "array-move";
import { useEffect, useState } from "react";
import { ArrayInput, Create, Datagrid, DeleteWithConfirmButton, Edit, EditButton, FormDataConsumer, List, RecordContextProvider, required, SaveButton, SelectInput, SimpleForm, SimpleFormIterator, TextInput, Toolbar, useDataProvider, useNotify, useRedirect, useRefresh } from "react-admin";
import { SortableContainer, SortableElement, SortableHandle } from "react-sortable-hoc";

const useStyles = makeStyles({
  trackingMapIteratorForm: {
    display: "flex",
    flex: "0 0 auto",
  },
  trackingMapIteratorAction: {
    paddingTop: "8px",
  },
  trackingMapInput: {
    display: "inline-block",
    marginRight: "16px",
    minWidth: "240px",
    verticalAlign: "top",
  },
});

const DatagridHeader = () => (
  <TableHead>
    <TableRow>
      <TableCell style={{ width: "50px" }}></TableCell> {/* Drag */}
      <TableCell style={{}}>Title in TC</TableCell>
      <TableCell style={{}}>Title in EN</TableCell>
      <TableCell style={{}}>Destination</TableCell>
      <TableCell style={{}}>Link</TableCell>
      <TableCell style={{}}>Link Branch</TableCell>
      <TableCell style={{}}>Tracking map</TableCell>
      <TableCell style={{}}></TableCell> {/* Edit */}
      <TableCell style={{}}></TableCell> {/* Remove */}
    </TableRow>
  </TableHead>
);

const DataGridEmptyView = () => {
  return (
    <div style={{ margin: "16px" }}>
      <p className="MuiTypography-root MuiTypography-body2">No results found</p>
    </div>
  );
};

const TrackingMapField = ({ trackingMap }) => {
  const entries = Array.isArray(trackingMap) ? trackingMap.map(({ label, value }) => [label, value]) : Object.entries(trackingMap || {});

  return entries.map(([label, value]) => (
    <div key={label}>
      {label}: {value}
    </div>
  ));
};

const MyDatagridBody = ({ allItems, setAllItems, data, ...props }) => {
  useEffect(() => {
    setAllItems(props.ids.map((id) => data[id]));
  }, [data, props.ids, setAllItems]);

  return allItems.map((item, index) => {
    return <SortableItem2 allItems={allItems} setAllItems={setAllItems} item={item} index={index} pos={index} {...props} />;
  });
};

const SortableItem2 = SortableElement(({ allItems, setAllItems, item, pos, ...props }) => {
  return <MyDatagridRow allItems={allItems} setAllItems={setAllItems} item={item} pos={pos} {...props} />;
});

const MyDatagridRow = ({ item }) => {
  return (
    <RecordContextProvider value={item}>
      <TableRow>
        <DragHandle />
        <TableCell style={{ overflowWrap: "break-word" }}>{item.title_tc}</TableCell>
        <TableCell style={{ overflowWrap: "break-word" }}>{item.title_en}</TableCell>
        <TableCell style={{ overflowWrap: "break-word" }}>{item.destination}</TableCell>
        <TableCell style={{ overflowWrap: "break-word" }}>{item.universal_link}</TableCell>
        <TableCell style={{ overflowWrap: "break-word" }}>{item.universal_link_branch_name}</TableCell>
        <TableCell style={{ overflowWrap: "break-word" }}>
          <TrackingMapField trackingMap={item.tracking_map} />
        </TableCell>
        <TableCell>
          <EditButton record={item} />
        </TableCell>

        <TableCell>{item.destination !== "HOME" && <DeleteWithConfirmButton record={item} mutationMode="pessimistic" />}</TableCell>
      </TableRow>
    </RecordContextProvider>
  );
};

const DragHandle = SortableHandle(() => (
  <TableCell>
    <ListItemIcon>
      <DragHandleIcon />
    </ListItemIcon>
  </TableCell>
));

const SortableListContainer = SortableContainer(({ allItems, setAllItems, ...props }) => {
  return (
    <List {...props} pagination={false} exporter={false}>
      <Datagrid //
        header={<DatagridHeader />}
        body={<MyDatagridBody allItems={allItems} setAllItems={setAllItems} {...props} />}
        empty={<DataGridEmptyView />}
      />
    </List>
  );
});

export const BottomMenuList = (props) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [allItems, setAllItems] = useState([]);

  const onSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex) {
      return;
    }

    const movedItem = allItems[oldIndex];
    setAllItems((allItems) => arrayMove(allItems, oldIndex, newIndex));

    dataProvider.updateBottomMenuOrder(movedItem.id, newIndex + 1).catch((error) => {
      notify(error.message.toString(), "warning");
      setAllItems((allItems) => arrayMove(allItems, newIndex, oldIndex));
    });
  };

  return (
    <SortableListContainer //
      {...props}
      allItems={allItems}
      setAllItems={setAllItems}
      onSortEnd={onSortEnd}
      useDragHandle={true}
      lockAxis="y"
      useWindowAsScrollContainer={true}
    />
  );
};

const BottomMenuToolbar = (props) => (
  <Toolbar {...props}>
    <SaveButton />
  </Toolbar>
);

const BottomMenuForm = (props) => {
  const classes = useStyles();

  return (
    <SimpleForm {...props} redirect={false} toolbar={<BottomMenuToolbar />}>
      <TextInput source="title_tc" label="Name in TC" validate={required()} />
      <TextInput source="title_en" label="Name in EN" validate={required()} />
      <SelectInput
        source="destination"
        label="Destination"
        choices={[
          { id: "HOME", name: "Home" },
          { id: "WEB", name: "Web" },
        ]}
        validate={required()}
      />
      <FormDataConsumer>{({ formData, ...rest }) => formData.destination === "WEB" && <TextInput source="universal_link" label="Link" validate={required()} {...rest} />}</FormDataConsumer>
      <FormDataConsumer>{({ formData, ...rest }) => formData.destination === "WEB" && <TextInput source="universal_link_branch_name" label="Link Branch" {...rest} />}</FormDataConsumer>
      <FormDataConsumer>
        {({ formData }) =>
          formData.destination === "WEB" && (
            <ArrayInput source="tracking_map" label="Tracking Map">
              {/* Add TransitionProps because of bug: https://github.com/marmelab/react-admin/issues/6900 */}
              <SimpleFormIterator
                inline
                disableReordering
                classes={{
                  form: classes.trackingMapIteratorForm,
                  action: classes.trackingMapIteratorAction,
                }}
                TransitionProps={{ enter: false, exit: false }}
              >
                <TextInput source="label" label="Label" validate={required()} formClassName={classes.trackingMapInput} fullWidth={false} />
                <TextInput source="value" label="Value" validate={required()} formClassName={classes.trackingMapInput} fullWidth={false} />
              </SimpleFormIterator>
            </ArrayInput>
          )
        }
      </FormDataConsumer>
    </SimpleForm>
  );
};

export const BottomMenuCreate = (props) => {
  const redirect = useRedirect();
  const refresh = useRefresh();
  const notify = useNotify();

  const onSuccess = () => {
    redirect("/bottom-menu");
    setTimeout(refresh, 0);
  };

  const onFailure = (error) => {
    notify(error.message.toString(), "warning");
  };

  return (
    <Create {...props} onSuccess={onSuccess} onFailure={onFailure} mutationMode="pessimistic">
      <BottomMenuForm />
    </Create>
  );
};

export const BottomMenuEdit = (props) => {
  const redirect = useRedirect();
  const refresh = useRefresh();

  const onSuccess = () => {
    redirect("/bottom-menu");
    setTimeout(refresh, 0);
  };

  return (
    <Edit {...props} onSuccess={onSuccess} mutationMode="pessimistic">
      <BottomMenuForm />
    </Edit>
  );
};
